// Auto-capture wardrobe items from positive purchase outcomes.
// Triggered after the user reports "perfect" fit on a purchased product.
//
// Flow:
//   1. Resolve a product image (passed in, or pulled from detected_items.products)
//   2. Skip if user already has a wardrobe row for this product_url
//   3. Run detect-wardrobe-item vision pass on the product image
//   4. Upload the image to the private `outfits` bucket and sign it (1y)
//   5. Insert a wardrobe_items row with rich attributes
//
// Failure modes are silent — callers may log but should never block the user.

import { supabase } from "@/integrations/supabase/client";
import { WARDROBE_SIGNED_URL_EXPIRY_SECONDS } from "@/lib/wardrobeImageUrl";
import { trackEvent } from "@/lib/analytics";

const CATEGORIES = new Set([
  "top",
  "bottom",
  "dress",
  "outerwear",
  "shoes",
  "bag",
  "accessory",
  "other",
]);

export interface AutoCaptureInput {
  userId: string;
  productUrl: string | null;
  productImage: string | null;
  productTitle: string;
  retailer: string | null;
  sizePurchased: string | null;
  category: string | null;
  detectedItemId: string | null;
}

export interface AutoCaptureResult {
  added: boolean;
  skipped_duplicate?: boolean;
  itemId?: string;
  error?: string;
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  for (let i = 0; i < buf.length; i++) binary += String.fromCharCode(buf[i]);
  // btoa is available in browsers
  return btoa(binary);
}

async function resolveProductImage(
  input: AutoCaptureInput,
): Promise<string | null> {
  if (input.productImage) return input.productImage;
  if (!input.detectedItemId) return null;
  const { data } = await (supabase.from("detected_items") as any)
    .select("products")
    .eq("id", input.detectedItemId)
    .maybeSingle();
  const products = (data?.products ?? []) as Array<Record<string, unknown>>;
  for (const p of products) {
    const thumb = (p?.thumbnail ?? p?.image ?? p?.image_url) as
      | string
      | undefined;
    if (thumb && typeof thumb === "string") return thumb;
  }
  return null;
}

export async function fetchAndUploadRetailerImage(
  retailerImageUrl: string,
  userId: string,
): Promise<{ url: string | null; path: string | null }> {
  try {
    const response = await fetch(retailerImageUrl);
    if (!response.ok) return { url: retailerImageUrl, path: null };

    const blob = await response.blob();
    const contentType = blob.type || "image/jpeg";
    const ext =
      contentType.split("/")[1]?.split("+")[0]?.replace("jpeg", "jpg") ?? "jpg";
    const fileName = `${crypto.randomUUID()}.${ext}`;
    const path = `${userId}/wardrobe/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from("outfits")
      .upload(path, blob, { contentType, upsert: false });

    if (uploadError) {
      console.warn("[auto-capture] image upload failed:", uploadError.message);
      return { url: retailerImageUrl, path: null };
    }

    const { data: signed } = await supabase.storage
      .from("outfits")
      .createSignedUrl(path, WARDROBE_SIGNED_URL_EXPIRY_SECONDS);

    return { url: signed?.signedUrl ?? retailerImageUrl, path };
  } catch (error) {
    console.warn("[auto-capture] image fetch/upload failed:", error);
    return { url: retailerImageUrl, path: null };
  }
}

export async function autoCaptureToWardrobe(
  input: AutoCaptureInput,
): Promise<AutoCaptureResult> {
  if (!input.userId) {
    return { added: false, error: "no_user" };
  }

  trackEvent("wardrobe_auto_capture_attempted", {
    outcome: "perfect",
    retailer: input.retailer,
    category: input.category,
  });

  // Step 1: Duplicate check by product URL
  if (input.productUrl) {
    const { data: existing } = await (supabase.from("wardrobe_items") as any)
      .select("id")
      .eq("user_id", input.userId)
      .eq("source_url", input.productUrl)
      .maybeSingle();

    if (existing) {
      trackEvent("wardrobe_auto_capture_skipped_duplicate", {
        retailer: input.retailer,
        product_url: input.productUrl,
      });
      return { added: false, skipped_duplicate: true };
    }
  }

  // Step 2: Resolve a product image
  const productImage = await resolveProductImage(input);
  if (!productImage) {
    trackEvent("wardrobe_auto_capture_failed", {
      reason: "no_product_image",
      retailer: input.retailer,
      category: input.category,
    });
    return { added: false, error: "no_product_image" };
  }

  // Step 3: Vision pass — fetch image → base64 → detect-wardrobe-item
  let detected: any = null;
  let detectionImageBlob: Blob | null = null;
  try {
    const imgResp = await fetch(productImage);
    if (!imgResp.ok) throw new Error(`fetch ${imgResp.status}`);
    detectionImageBlob = await imgResp.blob();
    const base64 = await blobToBase64(detectionImageBlob);
    const mediaType = detectionImageBlob.type || "image/jpeg";
    const { data, error: detectError } = await supabase.functions.invoke(
      "detect-wardrobe-item",
      { body: { image_base64: base64, media_type: mediaType } },
    );
    if (detectError || !data || (data as any).error) {
      throw new Error(
        (data as any)?.error ?? detectError?.message ?? "vision_failed",
      );
    }
    detected = data;
  } catch (e: any) {
    trackEvent("wardrobe_auto_capture_failed", {
      reason: "vision_failed",
      retailer: input.retailer,
      category: input.category,
    });
    return { added: false, error: e?.message ?? "vision_failed" };
  }

  // Step 4: Upload retailer image to storage so we can re-sign later
  const upload = await fetchAndUploadRetailerImage(productImage, input.userId);

  // Step 5: Insert wardrobe row
  const rawCategory = (detected.category ?? input.category ?? "other") as string;
  const category = CATEGORIES.has(rawCategory) ? rawCategory : "other";

  const insertPayload: Record<string, unknown> = {
    user_id: input.userId,
    name: detected.name ?? input.productTitle ?? "Untitled item",
    category,
    color_hex: detected.color_hex ?? null,
    material: detected.material ?? null,
    pattern: detected.pattern ?? null,
    silhouette: detected.silhouette ?? null,
    fit: detected.fit ?? null,
    length: detected.length ?? null,
    neckline: detected.neckline ?? null,
    sleeve_length: detected.sleeve_length ?? null,
    style_tags: Array.isArray(detected.style_tags) ? detected.style_tags : [],
    lb3_eligible: !!detected.lb3_eligible,
    lb3_description: detected.lb3_description ?? null,
    vision_description: detected.vision_description ?? null,
    formality: detected.formality ?? null,
    occasion_tags: Array.isArray(detected.occasion_tags) ? detected.occasion_tags : null,
    image_url: upload.url,
    image_path: upload.path,
    source_url: input.productUrl,
    source: "purchase_outcome",
    detected_item_id: input.detectedItemId,
    size_purchased: input.sizePurchased,
    is_active: true,
    times_used: 0,
  };

  const { data: inserted, error: insertError } = await (
    supabase.from("wardrobe_items") as any
  )
    .insert(insertPayload)
    .select("id")
    .single();

  if (insertError || !inserted) {
    trackEvent("wardrobe_auto_capture_failed", {
      reason: insertError?.message ?? "insert_failed",
      retailer: input.retailer,
      category,
    });
    return {
      added: false,
      error: insertError?.message ?? "insert_failed",
    };
  }

  const visionAttributeCount = [
    detected.material,
    detected.pattern,
    detected.silhouette,
    detected.fit,
    detected.length,
    detected.neckline,
    detected.sleeve_length,
    detected.vision_description,
  ].filter(Boolean).length;

  trackEvent("wardrobe_auto_capture_added", {
    item_id: inserted.id,
    retailer: input.retailer,
    category,
    has_image: !!upload.path,
    vision_attribute_count: visionAttributeCount,
  });

  return { added: true, itemId: inserted.id };
}

export async function undoWardrobeAdd(itemId: string): Promise<boolean> {
  const { error } = await supabase
    .from("wardrobe_items")
    .delete()
    .eq("id", itemId);

  if (error) {
    console.warn("[auto-capture] undo failed:", error.message);
    return false;
  }

  trackEvent("wardrobe_auto_capture_undone", { item_id: itemId });
  return true;
}
